import React, { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import NotFound from "@/pages/NotFound";
import { useSettings } from "@/context/SettingsContext";
import { pathToNavId, isHidden } from "@/lib/siteNav";

// Adds a subtle fade-up reveal to every page section as it scrolls into view (incl. async-loaded sections).
function useSectionReveal(ref, key) {
  useEffect(() => {
    const root = ref.current;
    if (!root || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); } });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.04 });
    // select every not-yet-visible section (incl. ones tagged .reveal by a previous/StrictMode effect run) so none stay hidden
    const scan = () => root.querySelectorAll(":scope section:not(.is-visible):not([data-no-reveal])").forEach((s) => {
      s.classList.add("reveal");
      io.observe(s);
    });
    scan();
    let t;
    const mo = new MutationObserver(() => { clearTimeout(t); t = setTimeout(scan, 60); });
    mo.observe(root, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); clearTimeout(t); };
  }, [ref, key]);
}

export default function PublicLayout() {
  const location = useLocation();
  const settings = useSettings();
  const navId = pathToNavId(location.pathname);
  const blocked = navId && isHidden(settings, navId);
  const mainRef = useRef(null);
  useSectionReveal(mainRef, location.pathname);

  return (
    <div className="App min-h-screen flex flex-col bg-npst-bg">
      <Header />
      <main ref={mainRef} key={location.pathname} className="flex-1 route-fade">
        {blocked ? <NotFound /> : <Outlet />}
      </main>
      <Footer />
    </div>
  );
}
